// The Walk's spine extraction (DECISIONS.md 2026-07-22 "Walk + Writer open
// design decisions"): the strongest path through an argument's reasoning
// subgraph, ranked by the VIEWER'S drawer weights — which live client-side
// (lensWeights.js), so the spine is computed here, not on the server. Pure
// functions over the /api/arguments/:id/structure payload; no new data.

// MIRROR of backend/trust/axes.ts (vector-trust phase 1, DECISIONS.md
// 2026-07-26): the client-side spine needs the same direction rule +
// activation boundary; consolidate onto the phase-2 axis metadata API.
export const edgeDir = (e) =>
  (e.relation_name === 'supports' ? 1 : e.relation_name === 'contradicts' ? -1 : 0) * e.polarity;
export const activated = (e) => e.up_votes + e.down_votes > 0;

// STANDING, for ranking only — three levels where there used to be two
// (2026-08-02, web-Claude research session on the Walk). `activated` is the
// activation boundary and is not being touched: it answers "has a human judged
// this", and a rejected edge HAS been judged. But ranking a fork by activation
// alone put a REJECTED edge above an unvoted one, so the spine could walk the
// one step the community had explicitly refused, in preference to a step nobody
// had ruled on yet. Endorsement, not attention, is what should promote a step.
//   2 = someone voted FOR it (includes contested — it is still testimony)
//   1 = inert: nobody has ruled, a suggestion
//   0 = every vote was against
// Deliberately the same cut as ProvenanceBadge's rejected state: one concept,
// so the badge on a step and the reason it was chosen cannot drift apart.
//
// EXPORTED 2026-08-06 for thread.js, which ranks an AUTHORED chain. The three
// helpers above are the ones a second traversal must not re-derive: a thread
// that disagreed with the Walk about what "rejected" means would drop a
// different set of steps for reasons no reader could see. thread.js departs
// from the Walk deliberately in one place only — it does not rank by the
// viewer's lens blend — and sharing these is what makes that the ONLY departure.
export const standing = (e) => (e.up_votes > 0 ? 2 : activated(e) ? 0 : 1);

// Walk from the root down its strongest supporting chain. Each step is
// { node, edge } where `edge` connects this node to the PREVIOUS step's node
// (null on the root). Rank at each fork: by `standing` above (endorsed, then
// inert, then rejected), then the premise's blended score under the viewer's
// weights, then raw edge votes. Spine follows `supports` only;
// premise_of/contradicts edges surface in the folds at each step.
export const isQuestionNode = (n) => !!n && n.object_type === 'question';

// Every type that carries NO trust reading — the frontend mirror of
// UNSCORED_OBJECT_TYPES in backend/trust/graph.ts, which is the authority.
//
// This exists because `question` stopped being the only one and the surfaces
// were still asking about it by name. The two states arrive at a renderer
// looking IDENTICAL — an object with empty scores — so a surface that cannot
// tell them apart shows "nothing has been judged here yet" over a thread, which
// is a lie in the most misleading direction available: it invites a reader to go
// and judge a thing that has nothing to judge.
//
// A SUBSET, deliberately, not a copy. `user`, `group` and `tag` are unscored on
// the backend too, but they never appear as a step or as a claim-shaped object
// page, and listing them here would imply this drives a code path they reach. A
// unit test pins that everything named here is unscored on the backend.
const UNSCORED_TYPES = new Set(['question', 'thread', 'heading', 'viewset', 'edge']);
export const isUnscoredNode = (n) => !!n && UNSCORED_TYPES.has(n.object_type);

export function extractSpine(rootId, nodes, edges, blendOf, maxSteps = 12) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const incoming = new Map(); // conclusion id → supporting edges into it
  // question id → the CANDIDATE ANSWERS put forward under it (2026-08-01).
  // `bears_on` runs claim → question, so answers arrive as incoming edges just
  // like supports — but they carry no direction and take no side. Stepping onto
  // one says "here is an answer someone put forward", never "this answer is
  // right"; the question itself stays scoreless and the claim underneath is
  // where the trust reading lives. Negated (`does not bear on`) is not a step.
  // `answers` (2026-08-02) is the stronger sibling, same direction and same
  // terms — it existed at the write door before the walk could see it.
  const answersTo = new Map();
  for (const e of edges) {
    if (e.relation_name === 'bears_on' || e.relation_name === 'answers') {
      if (e.polarity !== 1) continue;
      if (!answersTo.has(e.target)) answersTo.set(e.target, []);
      answersTo.get(e.target).push(e);
      continue;
    }
    if (edgeDir(e) <= 0) continue;
    if (!incoming.has(e.target)) incoming.set(e.target, []);
    incoming.get(e.target).push(e);
  }

  const root = byId.get(rootId);
  if (!root) return [];
  const steps = [{ node: root, edge: null }];
  const visited = new Set([rootId]);
  let current = rootId;
  while (steps.length < maxSteps) {
    // The step set depends on what we are STANDING ON: answers under a
    // question, supports under a claim. Same ranking either way — activated
    // before inert, then the viewer's blend, then raw votes.
    const from = isQuestionNode(byId.get(current)) ? answersTo : incoming;
    // standing > 0: a step the community has REFUSED is never walked, even
    // when it is the only candidate (2026-08-02, fixture `rejected-vs-inert`).
    // Walking the sole rejected edge would present it as the strongest line of
    // reasoning precisely because nothing credible exists — a fully-refused
    // fork is a dead end, and the fold's rejected count is where it shows.
    const candidates = (from.get(current) || [])
      .filter((e) => !visited.has(e.source) && byId.has(e.source) && standing(e) > 0)
      .sort(
        (a, b) =>
          standing(b) - standing(a) ||
          blendOf(byId.get(b.source)) - blendOf(byId.get(a.source)) ||
          b.vote_score - a.vote_score
      );
    if (!candidates.length) break;
    const e = candidates[0];
    visited.add(e.source);
    steps.push({ node: byId.get(e.source), edge: e });
    current = e.source;
  }
  return steps;
}

// The relations that put a CANDIDATE ANSWER under a question. Shared with the
// ballot partition below and with Argument.js, so the two cannot drift.
export const ANSWER_RELATIONS = new Set(['answers', 'bears_on']);

// Candidate answers under a question node that the spine did NOT step onto —
// the ballot (2026-08-02, fixture round 1). A question is where competing
// answers meet, and hiding the competition behind "N folded here" was the
// walk's single least intuitive rendering. Ranked exactly as the spine ranks,
// so the list reads as "the walk took the first of these that stood".
export function ballotFor(nodeId, edges, spineEdgeIds) {
  return edges
    .filter(
      (e) =>
        e.target === nodeId &&
        e.polarity === 1 &&
        ANSWER_RELATIONS.has(e.relation_name) &&
        !spineEdgeIds.has(e.id)
    )
    .sort((a, b) => standing(b) - standing(a) || b.vote_score - a.vote_score);
}

// Human-readable fold labels for relations that carry no direction — the fold
// used to say only "N folded here", which hid a qualifies exception and a
// sub-question equally well (2026-08-02, fixtures `qualifies` and
// `question-answers`). [singular, plural] per name; anything unlisted falls
// back to its own name with underscores opened.
const NAMED_FOLD = {
  qualifies: ['exception', 'exceptions'],
  crux_of: ['crux', 'cruxes'],
  is_subcategory_of: ['sub-question', 'sub-questions'],
  premise_of: ['premise', 'premises'],
  instance_of: ['instance', 'instances'],
  evidences: ['evidence', 'evidence'],
  counter_evidences: ['counter-evidence', 'counter-evidence'],
  restates: ['restatement', 'restatements'],
  answers: ['candidate answer', 'candidate answers'],
  bears_on: ['candidate answer', 'candidate answers'],
};

// Everything attached to a spine node that the spine itself doesn't show —
// objections fold at their attachment points. The fold signal is
// standing-aware (2026-08-02, fixture round 1): a REJECTED support used to
// count as "1 for", which read a refused edge as endorsement. Directional
// counts now require standing > 0; refused incoming edges count as
// `rejected`; zero-direction relations count under their own names.
// `isQuestion` excludes positive candidate-answer edges — those belong to the
// ballot (ballotFor above), not the fold.
export function foldFor(nodeId, edges, spineEdgeIds, isQuestion = false) {
  const folded = edges.filter(
    (e) =>
      (e.source === nodeId || e.target === nodeId) &&
      !spineEdgeIds.has(e.id) &&
      !(isQuestion && e.target === nodeId && e.polarity === 1 && ANSWER_RELATIONS.has(e.relation_name))
  );
  let forCount = 0;
  let againstCount = 0;
  let rejectedCount = 0;
  const namedCounts = new Map();
  for (const e of folded) {
    if (e.target !== nodeId) continue; // edges INTO the node take a side on it
    const d = edgeDir(e);
    if (d !== 0 && standing(e) === 0) {
      rejectedCount++;
    } else if (d > 0) {
      forCount++;
    } else if (d < 0) {
      againstCount++;
    } else {
      const names = NAMED_FOLD[e.relation_name] || [
        e.relation_name.replace(/_/g, ' '),
        e.relation_name.replace(/_/g, ' '),
      ];
      namedCounts.set(e.relation_name, {
        names,
        count: (namedCounts.get(e.relation_name)?.count || 0) + 1,
      });
    }
  }
  const named = [...namedCounts.values()].map(({ names, count }) => ({
    label: count === 1 ? names[0] : names[1],
    count,
  }));
  return { edges: folded, forCount, againstCount, rejectedCount, named };
}

// Blended-score range of the claims at a fold's far ends — the spread tick.
// Unscored nodes at a far end are DROPPED, not read as 0 (2026-08-01): they have
// no trust reading, and blendOf would hand back 0 for one, silently stretching
// the spread to include a score that was never computed. Same rule the tree
// already follows in refusing a spread tick on question rows.
//
// Widened from questions to the whole unscored family 2026-08-08, when threads
// became steppable and headings arrived: a section break at a fold's far end
// would otherwise pull the spread toward zero and make a run of agreeing claims
// read as contested.
export function foldSpread(nodeId, foldEdges, nodes, blendOf) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const blends = foldEdges
    .map((e) => byId.get(e.source === nodeId ? e.target : e.source))
    .filter((n) => n && !isUnscoredNode(n))
    .map(blendOf);
  if (!blends.length) return null;
  return { min: Math.min(...blends), max: Math.max(...blends) };
}
